import { useEffect, useState } from "react";
import { ClockCounterClockwise, DownloadSimple, SignIn, MagnifyingGlass } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function TeamActivityLog() {
  const [rows, setRows] = useState(null);
  const [team, setTeam] = useState([]);
  const [actor, setActor] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/audit", { params: { limit: 300 } }).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  if (!rows) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading activity log…</div>;

  const filtered = rows.filter((r) => {
    const okActor = actor === "all" || r.actor_id === actor;
    const s = q.toLowerCase();
    const okQ = !s || `${r.action} ${r.entity} ${r.detail || ""} ${r.actor_name || ""}`.toLowerCase().includes(s);
    return okActor && okQ;
  });
  const logins = rows.filter((r) => r.action === "auth.login");

  return (
    <div className="space-y-6" data-testid="team-activity-log-page">
      <PageHeader
        overline="Team" title="Team Activity Log"
        subtitle="Login history and actions performed per user — your accountability and security trail"
        action={<Button asChild variant="outline" className="rounded-full"><a href={`${API}/export/activity/log`} data-testid="export-audit-btn"><DownloadSimple size={16} className="mr-1" /> Export CSV</a></Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Logged events" value={rows.length} testid="stat-events" />
        <Stat label="Sign-ins recorded" value={logins.length} testid="stat-logins" />
        <Stat label="Active members" value={team.filter((t) => t.is_active !== false).length} testid="stat-active-members" />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actions…" className="pl-9" data-testid="log-search" />
        </div>
        <Select value={actor} onValueChange={setActor}>
          <SelectTrigger className="w-[220px]" data-testid="log-actor-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {ROLE_LABELS[m.role] || m.role}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={ClockCounterClockwise}>No matching log entries. Sign-ins and record changes get recorded here as they happen.</EmptyState>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((r) => (
            <Card key={r.id} className="p-3 flex items-start gap-3 border-border" data-testid={`log-${r.id}`}>
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${r.action === "auth.login" ? "bg-emerald-100 text-emerald-700" : "bg-primary/10 text-primary"}`}>
                {r.action === "auth.login" ? <SignIn size={15} /> : <ClockCounterClockwise size={15} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-primary">
                  <span className="font-medium">{r.actor_name || "Someone"}</span>{" "}
                  <span className="font-mono text-xs text-accent">{r.action}</span>
                  {r.entity ? <span className="text-muted-foreground text-xs"> · {r.entity}</span> : null}
                </div>
                {r.detail && <div className="text-xs text-muted-foreground mt-0.5">{r.detail}</div>}
              </div>
              <div className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                {new Date(r.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
